// Vista «Lista»: tabla densa en escritorio, tarjetas en el celular.
// ↑/↓ recorren las filas, Enter abre la ficha (cada fila es un botón).

import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Mail, Phone, UserX } from 'lucide-react';

import type { LeadDTO } from '@/modules/leads-import';
import { Avatar, prettyPhone } from '@/modules/whatsapp-app/components/shared';
import { assigneeLabel, isUnanswered, leadCompany, leadTitle, originLine, shortWhen, type StageKey } from '../model';
import { MoveMenu, StageChip } from './parts';

export function LeadsList({
  rows, names, activeId, onOpen, onMove, isMobile, compact, page, pageCount, onPage, rangeText, footnote, empty, loading,
}: {
  rows: LeadDTO[];
  names: Record<string, string>;
  activeId: string | null;
  onOpen: (l: LeadDTO) => void;
  onMove: (l: LeadDTO, s: StageKey) => void;
  isMobile: boolean;
  /** Con la ficha abierta a un lado: menos columnas. */
  compact: boolean;
  page: number;
  pageCount: number | null;
  onPage: (p: number) => void;
  rangeText: string;
  footnote?: string;
  empty: ReactNode;
  loading: boolean;
}) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const btns = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-lead]') || []);
    const i = btns.findIndex((b) => b === document.activeElement);
    const next = btns[Math.max(0, Math.min(btns.length - 1, i + (e.key === 'ArrowDown' ? 1 : -1)))];
    if (next) { e.preventDefault(); next.focus(); }
  };

  if (!loading && rows.length === 0) return <div className="la-scroll h-full overflow-y-auto p-4 sm:p-6">{empty}</div>;

  const pager = (
    <div className="flex items-center justify-between gap-3 border-t border-border bg-card px-3 py-2 text-[12.5px] sm:px-4">
      <span className="min-w-0 truncate text-muted-foreground tabular-nums">{rangeText}</span>
      <div className="flex shrink-0 items-center gap-1">
        <button type="button" onClick={() => onPage(page - 1)} disabled={page <= 0 || loading} aria-label="Página anterior"
          className="grid h-8 w-8 place-items-center rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
        <span className="px-1.5 text-muted-foreground tabular-nums">{page + 1}{pageCount ? ` / ${pageCount}` : ''}</span>
        <button type="button" onClick={() => onPage(page + 1)} disabled={(pageCount != null && page >= pageCount - 1) || loading} aria-label="Página siguiente"
          className="grid h-8 w-8 place-items-center rounded-lg border border-border text-foreground hover:bg-muted disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <div ref={listRef} onKeyDown={onKey} className="la-scroll min-h-0 flex-1 overflow-y-auto" aria-busy={loading || undefined}>
          <ul className="divide-y divide-border">
            {rows.map((l) => (
              <li key={l.id}>
                <button type="button" data-lead onClick={() => onOpen(l)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left ${activeId === l.id ? 'la-row-active' : 'hover:bg-muted/50'}`}>
                  <Avatar name={leadTitle(l)} size={40} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className="min-w-0 flex-1 truncate text-[14.5px] font-semibold text-foreground">{leadTitle(l)}</span>
                      <span className="shrink-0 text-[11.5px] text-muted-foreground tabular-nums">{shortWhen(l.created_at)}</span>
                    </span>
                    <span className="mt-0.5 block truncate text-[12.5px] text-muted-foreground">
                      {[l.phone && prettyPhone(l.phone), l.email].filter(Boolean).join(' · ') || 'Sin contacto'}
                    </span>
                    <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      <StageChip status={l.status} />
                      {originLine(l) && <span className="max-w-[60%] truncate text-[11.5px] text-muted-foreground">{originLine(l)}</span>}
                      {isUnanswered(l) && <span className="text-[11.5px] font-medium text-destructive">· sin respuesta</span>}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {footnote && <p className="px-4 py-3 text-[11.5px] text-muted-foreground">{footnote}</p>}
        </div>
        {pager}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div ref={listRef} onKeyDown={onKey} className="la-scroll min-h-0 flex-1 overflow-auto" aria-busy={loading || undefined}>
        <table className="w-full table-fixed text-[13px]">
          <thead className="sticky top-0 z-10 bg-card text-left text-[11.5px] font-medium text-muted-foreground shadow-[inset_0_-1px_0_hsl(var(--border))]">
            <tr>
              <th scope="col" className="w-[28%] px-4 py-2.5 font-medium">Lead</th>
              {!compact && <th scope="col" className="w-[20%] px-3 py-2.5 font-medium">Contacto</th>}
              <th scope="col" className="w-[118px] px-3 py-2.5 font-medium">Etapa</th>
              {!compact && <th scope="col" className="px-3 py-2.5 font-medium">Origen</th>}
              <th scope="col" className="w-[16%] px-3 py-2.5 font-medium">Vendedora</th>
              <th scope="col" className="w-[84px] px-3 py-2.5 text-right font-medium">Llegó</th>
              <th scope="col" className="w-[52px] px-2 py-2.5"><span className="sr-only">Acciones</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {rows.map((l) => {
              const who = assigneeLabel(l, names);
              return (
                <tr key={l.id} data-lead tabIndex={0} aria-selected={activeId === l.id}
                  onClick={() => onOpen(l)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(l); } }}
                  className={`cursor-pointer outline-none transition-colors focus-visible:bg-muted ${activeId === l.id ? 'la-row-active' : 'hover:bg-muted/50'}`}>
                  <td className="px-4 py-2">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <Avatar name={leadTitle(l)} size={32} />
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-foreground">{leadTitle(l)}</div>
                        <div className="truncate text-[12px] text-muted-foreground">
                          {compact ? ([l.phone && prettyPhone(l.phone), l.email].filter(Boolean).join(' · ') || leadCompany(l) || '—') : (leadCompany(l) || l.message || '—')}
                        </div>
                      </div>
                    </div>
                  </td>
                  {!compact && (
                    <td className="px-3 py-2 text-[12.5px] text-foreground/85">
                      {l.phone && <div className="flex min-w-0 items-center gap-1.5"><Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden /><span className="truncate tabular-nums">{prettyPhone(l.phone)}</span></div>}
                      {l.email && <div className="flex min-w-0 items-center gap-1.5"><Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden /><span className="truncate">{l.email}</span></div>}
                      {!l.phone && !l.email && <span className="text-muted-foreground">—</span>}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <StageChip status={l.status} />
                    {isUnanswered(l) && <div className="mt-0.5 text-[11px] font-medium text-destructive">sin respuesta</div>}
                  </td>
                  {!compact && (
                    <td className="truncate px-3 py-2 text-[12.5px] text-muted-foreground" title={originLine(l)}>
                      {originLine(l) || <span className="italic">Sin registrar</span>}
                    </td>
                  )}
                  <td className="truncate px-3 py-2 text-[12.5px]">
                    {who ? <span className="text-foreground/85">{who}</span>
                      : <span className="inline-flex max-w-full items-center gap-1 whitespace-nowrap text-muted-foreground"><UserX className="h-3.5 w-3.5 shrink-0" aria-hidden />Sin asignar</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-[12px] text-muted-foreground tabular-nums">{shortWhen(l.created_at)}</td>
                  <td className="px-2 py-2 text-right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <MoveMenu current={l.status} onMove={(s) => onMove(l, s)} compact />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {footnote && <p className="px-4 py-3 text-[11.5px] text-muted-foreground">{footnote}</p>}
      </div>
      {pager}
    </div>
  );
}
